// Building interiors: a small room per building with stations you walk up to.
// Rooms are centered at the origin; the door is on the south wall (+z), facing the camera.

import type { ZoneAction } from "./districts";
import { CARS, DEALERS, type DealerId } from "@degen/sim/cars/cars";
import { FURNITURE, footprint, type HouseListing, type Placement } from "@degen/sim/houses/houses";

export interface Station {
  id: string;
  name: string;
  x: number;
  z: number;
  r: number;
  actions: ZoneAction[];
}

export interface Interior {
  id: string; // matches the outside zone id it is entered from
  name: string;
  w: number; // room width (x)
  d: number; // room depth (z)
  floor: string;
  wall: string;
  accent: string;
  npcs: number; // people hanging out inside
  stations: Station[];
}

const exit = (d: number): Station => ({ id: "door", name: "Door", x: 0, z: d / 2 - 0.9, r: 1.6, actions: ["exit"] });

export const INTERIORS: Record<string, Interior> = {
  home: {
    id: "home", name: "Your apartment", w: 12, d: 10, floor: "#c8a27a", wall: "#efe3cf", accent: "#7c3aed", npcs: 0,
    stations: [
      { id: "bed", name: "Bed", x: -3.8, z: -2.6, r: 1.8, actions: ["sleep", "laylow"] },
      { id: "desk", name: "Trading desk", x: 3.6, z: -3, r: 1.6, actions: ["pumpr", "wallet", "chirp"] },
      { id: "petbed", name: "Pet corner", x: 4, z: 1.8, r: 1.6, actions: ["pets"] },
      { id: "shelf", name: "Trophy shelf", x: 0, z: -3.6, r: 1.4, actions: ["profile", "settings"] },
      exit(10),
    ],
  },
  cafe: {
    id: "cafe", name: "Café", w: 12, d: 10, floor: "#8b6b4a", wall: "#f5e6c8", accent: "#b91c1c", npcs: 4,
    stations: [
      { id: "counter", name: "Counter", x: 0, z: -3, r: 1.8, actions: ["coffee"] },
      { id: "tables", name: "Tables", x: -3, z: 1, r: 2, actions: ["chirp"] },
      { id: "board", name: "Notice board", x: 4.5, z: -3.5, r: 1.4, actions: ["farm", "mintr"] },
      exit(10),
    ],
  },
  trading: {
    id: "trading", name: "Trading Floor", w: 16, d: 12, floor: "#1f2937", wall: "#334155", accent: "#22c55e", npcs: 6,
    stations: [
      { id: "wall", name: "Ticker wall", x: 0, z: -4.4, r: 2.4, actions: ["pumpr"] },
      { id: "desks", name: "Trading desks", x: -4, z: 0, r: 2.4, actions: ["pumpr", "wallet"] },
      { id: "launch", name: "Launch desk", x: 4.5, z: 0.5, r: 1.8, actions: ["launch_coin"] },
      exit(12),
    ],
  },
  mint: {
    id: "mint", name: "Mintr Gallery", w: 14, d: 10, floor: "#e5e7eb", wall: "#ede9fe", accent: "#a855f7", npcs: 3,
    stations: [
      { id: "frames", name: "Gallery wall", x: 0, z: -3.6, r: 2.4, actions: ["mintr"] },
      { id: "pedestal", name: "Launch pedestal", x: 0, z: 0.5, r: 1.6, actions: ["launch_nft"] },
      exit(10),
    ],
  },
  farm: {
    id: "farm", name: "The Barn", w: 12, d: 10, floor: "#a16207", wall: "#7f1d1d", accent: "#facc15", npcs: 2,
    stations: [
      { id: "desks", name: "Protocol desks", x: 0, z: -3, r: 2.2, actions: ["farm"] },
      { id: "hay", name: "Hay bales", x: -4, z: 1.5, r: 1.6, actions: ["grass"] },
      exit(10),
    ],
  },
  petshop: {
    id: "petshop", name: "Pet Shop", w: 12, d: 9, floor: "#fde68a", wall: "#fef3c7", accent: "#f97316", npcs: 1,
    stations: [
      { id: "counter", name: "Adoption counter", x: 0, z: -2.8, r: 1.8, actions: ["adopt", "pets"] },
      exit(9),
    ],
  },
  missioncontrol: {
    id: "missioncontrol", name: "Mission Control", w: 14, d: 10, floor: "#1e293b", wall: "#cbd5e1", accent: "#ef4444", npcs: 3,
    stations: [
      { id: "consoles", name: "Launch consoles", x: 0, z: -3, r: 2.4, actions: ["launch_coin", "launch_nft"] },
      { id: "charts", name: "Chart wall", x: -4.6, z: 0.5, r: 1.6, actions: ["pumpr"] },
      exit(10),
    ],
  },
  vano: {
    id: "vano", name: "Vano HQ", w: 14, d: 11, floor: "#0f0a1f", wall: "#1e1b4b", accent: "#a855f7", npcs: 3,
    stations: [
      { id: "core", name: "Vano core", x: 0, z: -3.2, r: 2.2, actions: ["vano"] },
      { id: "terminals", name: "Signal terminals", x: 4.5, z: 0.5, r: 1.8, actions: ["vano", "pumpr"] },
      exit(11),
    ],
  },
  gadgets: {
    id: "gadgets", name: "Plug Gadgets", w: 13, d: 9, floor: "#f8fafc", wall: "#e2e8f0", accent: "#0ea5e9", npcs: 2,
    stations: [
      { id: "counter", name: "Phone counter", x: 0, z: -2.6, r: 2.2, actions: ["gadgets"] },
      exit(9),
    ],
  },
  hackhub: {
    id: "hackhub", name: "Hackathon Hub", w: 16, d: 12, floor: "#334155", wall: "#475569", accent: "#22c55e", npcs: 6,
    stations: [
      { id: "desks", name: "Build desks", x: -3.5, z: -0.5, r: 2.6, actions: ["hackathon"] },
      { id: "stage", name: "Demo stage", x: 4, z: -3.6, r: 2, actions: ["hackathon", "chirp"] },
      exit(12),
    ],
  },
  arena: {
    id: "arena", name: "Event Arena", w: 20, d: 14, floor: "#134e4a", wall: "#0f172a", accent: "#f472b6", npcs: 10,
    stations: [
      { id: "stage", name: "Main stage", x: 0, z: -4.5, r: 2.6, actions: ["events", "chirp"] },
      { id: "booths", name: "Sponsor booths", x: -6.5, z: 1, r: 2, actions: ["events"] },
      exit(14),
    ],
  },
  lighthouse: {
    id: "lighthouse", name: "Lighthouse", w: 9, d: 9, floor: "#e7e5e4", wall: "#f5f5f4", accent: "#38bdf8", npcs: 0,
    stations: [
      { id: "window", name: "Sea window", x: 0, z: -3, r: 2, actions: ["grass", "laylow"] },
      exit(9),
    ],
  },
};

export function stationAt(room: Interior, x: number, z: number): Station | null {
  let best: Station | null = null;
  let bd = Infinity;
  for (const s of room.stations) {
    const d = Math.hypot(x - s.x, z - s.z);
    if (d <= s.r && d < bd) {
      best = s;
      bd = d;
    }
  }
  return best;
}

// ---------------------------------------------------------------- dealerships


export interface DisplayCar {
  modelId: string;
  color: string;
  x: number;
  z: number;
  sx: number; // where to stand to look at it (offset)
  sz: number;
}

/** Showroom: up to 6 cars on display, two rows. Stations are the cars themselves. */
export function dealerInterior(id: DealerId): { def: Interior; cars: DisplayCar[] } {
  const models = CARS.filter((c) => c.dealer === id);
  // Sides and back wall, leaving a clear aisle from the door up the middle.
  const SLOTS = [
    { x: -7.5, z: -3.6, sx: 2.9, sz: 0 }, { x: -7.5, z: 1.8, sx: 2.9, sz: 0 },
    { x: 7.5, z: -3.6, sx: -2.9, sz: 0 }, { x: 7.5, z: 1.8, sx: -2.9, sz: 0 },
    { x: -2.7, z: -4, sx: 0, sz: 2.8 }, { x: 2.7, z: -4, sx: 0, sz: 2.8 },
  ];
  const cars = models.map((m, i) => ({ modelId: m.id, color: m.colors[0], x: SLOTS[i].x, z: SLOTS[i].z, sx: SLOTS[i].sx, sz: SLOTS[i].sz }));
  const accent = id === "polancco" ? "#0284c7" : id === "abbja" ? "#a21caf" : id === "zoomers" ? "#f97316" : "#d97706";
  const def: Interior = {
    id,
    name: DEALERS[id].name,
    w: 22,
    d: 14,
    floor: id === "abbja" ? "#d6d3d1" : id === "zoomers" ? "#27272a" : "#e5e7eb",
    wall: id === "mrjaay" ? "#1c1917" : id === "zoomers" ? "#3f3f46" : "#f8fafc",
    accent,
    npcs: 3,
    stations: [
      ...cars.map((c) => ({ id: c.modelId, name: `${CARS.find((m) => m.id === c.modelId)!.make} ${CARS.find((m) => m.id === c.modelId)!.model}`, x: c.x + c.sx, z: c.z + c.sz, r: 1.5, actions: ["cars" as const, "testdrive" as const] })),
      { id: "desk", name: "Sales desk", x: 0, z: 0.4, r: 1.6, actions: ["cars" as const] },
      { id: "door", name: "Door", x: 0, z: 14 / 2 - 0.9, r: 1.6, actions: ["exit" as const] },
    ],
  };
  return { def, cars };
}

// ---------------------------------------------------------------- houses (from your layout)

export const CELL = 1.4; // world units per layout cell

export interface FurnitureBox {
  item: string;
  x: number; // world center
  z: number;
  w: number;
  d: number;
  color: string;
}

/** Build a room from a house listing + your saved layout (empty if you're touring it). */
export function houseInterior(l: HouseListing, layout: Placement[], owned: boolean): { def: Interior; furniture: FurnitureBox[] } {
  const w = l.gw * CELL;
  const d = l.gd * CELL;
  const toWorld = (cx: number, cz: number) => ({ x: -w / 2 + cx * CELL, z: -d / 2 + cz * CELL });
  const furniture: FurnitureBox[] = [];
  const stations: Station[] = [];
  layout.forEach((p, i) => {
    const f = FURNITURE.find((x) => x.id === p.item)!;
    const fp = footprint(p);
    const tl = toWorld(p.x, p.z);
    const cx = tl.x + (fp.w * CELL) / 2;
    const cz = tl.z + (fp.d * CELL) / 2;
    furniture.push({ item: f.id, x: cx, z: cz, w: fp.w * CELL, d: fp.d * CELL, color: f.color });
    if (owned && f.actions?.length) stations.push({ id: `f${i}`, name: f.name, x: cx, z: cz + (fp.d * CELL) / 2 + 0.6, r: Math.max(1.3, (Math.max(fp.w, fp.d) * CELL) / 2), actions: f.actions });
  });
  if (!owned) stations.push({ id: "forsale", name: `${l.name} · for sale`, x: 0, z: 0, r: 2.5, actions: ["houses"] });
  else stations.push({ id: "designer", name: "Home designer", x: w / 2 - 1.2, z: d / 2 - 1.6, r: 1.2, actions: ["houses", "messages"] });
  stations.push({ id: "door", name: "Door", x: 0, z: d / 2 - 0.9, r: 1.6, actions: ["exit"] });
  return {
    def: { id: l.id, name: l.name, w, d, floor: l.floor, wall: l.wall, accent: "#f97316", npcs: 0, stations },
    furniture,
  };
}
